import { computed, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService, AuthStore, UserTypeEnum } from '@aurrica-web/core-data';
import {
  Observable,
  catchError,
  filter,
  isObservable,
  map,
  of,
  switchMap,
  take,
} from 'rxjs';

import { hasStoredSession } from './auth-guard';

const TERMS_ACCEPT_PATH = '/confirm-terms-accept';

/** Narrow store surface used by {@link termsGuard} (avoids typing `signalStore` instance). */
interface AuthStoreForTerms {
  sub: () => string;
  isAuthenticated: () => boolean;
  userType: () => string;
  termsAccepted: () => boolean;
  'custom:terms_hash'?: () => string | undefined;
}

function needsHydrationWaitSnapshot(s: {
  sub: string;
  isAuthenticated: boolean;
}): boolean {
  /** Tokens in storage but Cognito profile not merged yet (session restore before `getUser`). */
  return Boolean(
    hasStoredSession() && s.isAuthenticated && s.sub.length === 0,
  );
}

function wrapResult(
  v: boolean | UrlTree | Observable<boolean | UrlTree>,
): Observable<boolean | UrlTree> {
  return isObservable(v) ? v : of(v);
}

/**
 * Runs after Cognito profile fields (including `custom:terms_hash`) are on the store.
 */
function termsDecision(
  router: Router,
  authService: AuthService,
  authStore: AuthStoreForTerms,
): boolean | UrlTree | Observable<boolean | UrlTree> {
  const termsHash = authStore['custom:terms_hash']?.() ?? '';

  const isAdmin =
    (authStore.userType() as UserTypeEnum) === UserTypeEnum.SUPER_ADMIN;

  if (isAdmin) {
    return true;
  }

  if (authStore.termsAccepted()) {
    return true;
  }

  const authed = authStore.isAuthenticated() || hasStoredSession();
  if (!authed) {
    return router.parseUrl('/login');
  }

  if (!termsHash) {
    return router.parseUrl(TERMS_ACCEPT_PATH);
  }

  return authService.getTermsHash().pipe(
    map((res) => {
      if (res.sha256 === termsHash) {
        return true;
      }
      return router.parseUrl(TERMS_ACCEPT_PATH);
    }),
    catchError(() => of(router.parseUrl(TERMS_ACCEPT_PATH))),
  );
}

/**
 * Ensures the user has accepted the current terms before entering protected areas.
 *
 * @remarks
 * - Waits until Cognito profile is on the store (`sub` set) when a browser session exists
 *   but the user slice has not been hydrated yet (avoids false redirects right after reload).
 * - Skips check for `UserTypeEnum.SUPER_ADMIN`.
 * - If `AuthStore.termsAccepted()` is true (same session after accept), allows navigation.
 * - If the user has no Cognito `custom:terms_hash`, sends them to `/confirm-terms-accept`.
 * - Otherwise compares the stored hash with `AuthService.getTermsHash()`; on mismatch or error,
 *   redirects to `/confirm-terms-accept`.
 */
export const termsGuard: CanActivateFn = ():
  | boolean
  | UrlTree
  | Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);
  const injector = inject(Injector);

  if (
    !needsHydrationWaitSnapshot({
      sub: authStore.sub(),
      isAuthenticated: authStore.isAuthenticated(),
    })
  ) {
    return wrapResult(termsDecision(router, authService, authStore));
  }

  const snapshot = computed(() => ({
    sub: authStore.sub(),
    isAuthenticated: authStore.isAuthenticated(),
  }));

  return toObservable(snapshot, { injector }).pipe(
    filter((s) => !needsHydrationWaitSnapshot(s)),
    take(1),
    switchMap(() => wrapResult(termsDecision(router, authService, authStore))),
  );
};
